import React, { useEffect, useRef, useState, useId, type JSX } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import { IconError } from '@/app/common/icons/libraryIcons';
import { useDropdownSide } from '@/app/common/hooks/useDropdownSide.ts';
import { scrollTopRevealing } from '@/app/common/utils/revealScrollTop.ts';
import { useTranslation } from '@/app/components/translations/Translation';
import {
  AutoCompleteContainer,
  AutoCompleteRoot,
  AutoCompleteInner,
  AutocompleteTagDelete,
  NoSuggestions,
  SuggestionContainer,
  SuggestionInput,
  SuggestionsContainer,
  SuggestionsContainerOuter,
  SuggestionsLabel,
} from './styled';
import MagnifyingGlass from './Icons/MagnifyingGlass';

export type SuggestionId = string;

export type SuggestionValue = string;

export type Suggestion = {
  id: SuggestionId;
  value: SuggestionValue;
  suggestionContent: string | JSX.Element;
};
export type Suggestions = Suggestion[];

export type SuggestionFetcher = (query: string) => Promise<Suggestions>;

export type SuggestionResult = SuggestionValue;

export type AutoCompleteBasicProps = {
  suggestionFetcher: SuggestionFetcher;
  initialValue?: SuggestionValue;
  placeholder?: string;
  noSuggestionsLabel?: string;
  focusOnLoad?: boolean;
  numCharsToFetchAfter: number;
  isFullScreen?: boolean;
  disabled?: boolean;
  evo?: boolean;
  onSelected: (selectedSuggestion: SuggestionResult | undefined) => void;
};

let timeout: string | number | NodeJS.Timeout | undefined;

const AutoCompleter = ({
  suggestionFetcher,
  initialValue = '',
  placeholder = '',
  noSuggestionsLabel = '',
  focusOnLoad,
  numCharsToFetchAfter = 3,
  isFullScreen = false,
  onSelected,
  disabled = false,
  evo = false,
}: AutoCompleteBasicProps): JSX.Element => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const [chosenSuggestion, setChosenSuggestion] = useState<SuggestionResult>(initialValue);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(-1);
  const [directionDown, setDirectionDown] = useState<boolean>(true);
  const [suggestions, setSuggestions] = useState<Suggestions>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [userInput, setUserInput] = useState<string>(initialValue);
  const [allowFetch, setAllowFetch] = useState<boolean>(true);
  const [focus, setFocus] = useState<boolean>(false);
  const inputRef = React.useRef<HTMLInputElement | undefined>(undefined);
  const hasUserInput = !!userInput;
  const inputId = useId();
  const evoInline = evo && !isFullScreen;
  const showsValueAlone = evo && hasUserInput && !focus && userInput === initialValue;
  const showsSuggestionList = showSuggestions && hasUserInput && !!suggestions?.length;
  const showsNoSuggestions = showSuggestions && hasUserInput && !showsSuggestionList && !!noSuggestionsLabel;
  const popupId = `${inputId}-popup`;
  const optionId = (index: number) => `${inputId}-option-${index}`;
  const fieldRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const side = useDropdownSide(
    fieldRef,
    dropdownRef,
    evoInline && (showsSuggestionList || showsNoSuggestions),
    showsSuggestionList ? suggestions.length : 0
  );

  const focusInput = () => {
    if (inputRef?.current) inputRef.current.focus();
  };

  const blurInput = () => {
    if (inputRef?.current) inputRef.current.blur();
  };

  useEffect(() => {
    if (focusOnLoad) {
      focusInput();
    }
  }, []);

  useEffect(() => {
    if (!initialValue) {
      setUserInput('');
    }
  }, [initialValue]);

  useEffect(() => {
    if (activeSuggestionIndex >= 0 && containerRef?.current) {
      const li = containerRef.current.getElementsByTagName('li');
      if (activeSuggestionIndex < li.length) {
        if (evo && dropdownRef.current) {
          dropdownRef.current.scrollTop = scrollTopRevealing(dropdownRef.current, li[activeSuggestionIndex]);
          return;
        }
        // @ts-ignore
        if (li[activeSuggestionIndex].scrollIntoViewIfNeeded) {
          // @ts-ignore
          li[activeSuggestionIndex].scrollIntoViewIfNeeded(!directionDown);
        } else {
          li[activeSuggestionIndex].scrollIntoView(!directionDown);
        }
      }
    }
  }, [activeSuggestionIndex, evo]);

  const resetSelection = (value = '') => {
    setChosenSuggestion(value);
    setActiveSuggestionIndex(-1);
    setUserInput(value);
    setAllowFetch(true);
  };

  const closeAuto = (): void => {
    if (!chosenSuggestion || userInput !== chosenSuggestion) {
      resetSelection(initialValue);
    }
    setShowSuggestions(() => false);
  };

  useOnclickOutside(
    () => {
      setShowSuggestions(() => false);
      setAllowFetch(true);
      setFocus(false);
    },
    { refs: [containerRef] }
  );

  const handleSelected = (suggestion: Suggestion | undefined): void => {
    const nextValue = suggestion?.value || '';
    if (nextValue && nextValue !== chosenSuggestion) {
      onSelected(nextValue);
    }
    setChosenSuggestion(nextValue);
    setActiveSuggestionIndex(-1);
    setShowSuggestions(false);
    if (suggestion?.value) {
      setUserInput(suggestion?.value);
    } else {
      setUserInput('');
      setAllowFetch(true);
    }
  };

  const fetchSuggestions = (query: string, startIndex = -1) => {
    clearTimeout(timeout);
    timeout = setTimeout(async () => {
      const matchingSuggestions = await suggestionFetcher(query);
      if (matchingSuggestions?.length === 0) {
        setAllowFetch(false);
      }

      setActiveSuggestionIndex(() => startIndex);
      setSuggestions(() => matchingSuggestions);
      setShowSuggestions(() => true);
    }, 500);
  };

  const pasted = (e: React.KeyboardEvent<HTMLInputElement>) =>
    (e.ctrlKey && e.key.toLowerCase() === 'v') || (e.metaKey && e.key.toLowerCase() === 'v');

  const cut = (e: React.KeyboardEvent<HTMLInputElement>) =>
    (e.ctrlKey && e.key.toLowerCase() === 'x') || (e.metaKey && e.key.toLowerCase() === 'x');

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      if (showsSuggestionList && activeSuggestionIndex >= 0) {
        const suggestion = suggestions[activeSuggestionIndex];
        handleSelected(suggestion);
      } else {
        clearTimeout(timeout);
        if (userInput && userInput !== chosenSuggestion) {
          onSelected(userInput);
        }
        setChosenSuggestion(userInput || '');
        setActiveSuggestionIndex(-1);
        setShowSuggestions(false);
        blurInput();
      }
    } else if (e.key === 'ArrowUp') {
      if (activeSuggestionIndex <= 0) {
        return;
      }
      setDirectionDown(false);
      setActiveSuggestionIndex(activeSuggestionIndex - 1);
    } else if (e.key === 'ArrowDown') {
      // The suggestions kept from an earlier search may not match the text now in the field, so search again.
      if (!showsSuggestionList) {
        if (allowFetch && userInput.length >= numCharsToFetchAfter) fetchSuggestions(userInput, 0);
        return;
      }
      if (activeSuggestionIndex + 1 === suggestions.length) {
        return;
      }
      setDirectionDown(true);
      setActiveSuggestionIndex(activeSuggestionIndex + 1);
    } else if (e.key === 'Escape') {
      closeAuto();
    } else if (e.key === 'Tab') {
      setShowSuggestions(false);
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      setAllowFetch(true);
    } else if (cut(e)) {
      setAllowFetch(true);
    } else if (pasted(e)) {
      setAllowFetch(true);
    }
  };

  const handleInputChanged = async (e: React.FormEvent<HTMLInputElement>) => {
    const userInputValue = e.currentTarget.value;

    setUserInput(userInputValue);

    if (userInputValue.length >= numCharsToFetchAfter && allowFetch) {
      fetchSuggestions(userInputValue);
    }
  };

  const handleInputOnFocus = () => {
    setChosenSuggestion('');
    setActiveSuggestionIndex(-1);
    setAllowFetch(true);
    setFocus(true);
  };

  const handleInputOnBlur = () => {
    setFocus(false);
  };

  const handleSuggestionClicked = (e: React.MouseEvent<HTMLElement>): void => {
    const suggestionId = e.currentTarget.getAttribute('data-id');
    const suggestion = suggestions.find((s) => s.id === suggestionId);
    handleSelected(suggestion);
  };

  const handleDeleteClicked = () => {
    resetSelection();
    focusInput();
    onSelected('');
  };

  const handleInnerClicked = () => {
    focusInput();
  };

  let suggestionsListComponent;

  if (showsSuggestionList) {
    suggestionsListComponent = (
      <SuggestionsContainerOuter
        ref={dropdownRef}
        $evo={evo}
        data-side={side}
        isFullScreen={isFullScreen}
        className="suggestions-dropdown"
      >
        <SuggestionsContainer
          id={popupId}
          role="listbox"
          aria-label={placeholder}
          isFullScreen={isFullScreen}
          $evo={evo}
        >
          {suggestions.map((suggestion, index) => {
            const activeClassName = index === activeSuggestionIndex ? 'suggestion-active' : '';

            return (
              <SuggestionContainer
                $evo={evo}
                className={activeClassName}
                key={suggestion.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeSuggestionIndex}
                data-id={suggestion.id}
                onClick={handleSuggestionClicked}
              >
                {suggestion.suggestionContent}
              </SuggestionContainer>
            );
          })}
        </SuggestionsContainer>
      </SuggestionsContainerOuter>
    );
  } else if (showsNoSuggestions) {
    suggestionsListComponent = (
      <NoSuggestions
        ref={dropdownRef}
        $evo={evo}
        data-side={side}
        className="suggestions-dropdown"
        isFullScreen={isFullScreen}
        role="status"
        aria-live="polite"
      >
        <em>{noSuggestionsLabel}</em>
      </NoSuggestions>
    );
  }

  return (
    <AutoCompleteRoot ref={containerRef} isFullScreen={isFullScreen}>
      <AutoCompleteContainer isFullScreen={isFullScreen}>
        <AutoCompleteInner
          ref={fieldRef}
          $evo={evo}
          isFullScreen={isFullScreen}
          onClick={handleInnerClicked}
          focus={focus}
        >
          <MagnifyingGlass size={evo ? 20 : 19} />
          <SuggestionsLabel
            isFullScreen={isFullScreen}
            focus={focus || hasUserInput}
            $hidden={showsValueAlone}
            $evo={evo}
            htmlFor={inputId}
          >
            {placeholder}
          </SuggestionsLabel>
          <SuggestionInput
            id={inputId}
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={showsSuggestionList}
            aria-autocomplete="list"
            aria-controls={showsSuggestionList ? popupId : undefined}
            aria-activedescendant={
              showsSuggestionList && activeSuggestionIndex >= 0 ? optionId(activeSuggestionIndex) : undefined
            }
            onChange={handleInputChanged}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputOnFocus}
            onBlur={evo ? handleInputOnBlur : undefined}
            value={userInput}
            isFullScreen={isFullScreen}
            focus={focus}
            $evo={evo}
            $centred={showsValueAlone}
            disabled={disabled}
          />
          {userInput && (
            <AutocompleteTagDelete
              type="button"
              className="oss-ui-autocomplete-tag-close"
              onClick={handleDeleteClicked}
              isFullScreen={isFullScreen}
              $evo={evo}
              aria-label={t('vacation.oss.filters.clear')}
            >
              <IconError size={evo ? 16 : 18} />
            </AutocompleteTagDelete>
          )}
        </AutoCompleteInner>
      </AutoCompleteContainer>
      {suggestionsListComponent}
    </AutoCompleteRoot>
  );
};

export default AutoCompleter;
